import { WarningOctagon } from '@phosphor-icons/react'
import useSessionStore from 'Context/useSessionStore'
import { signOut } from 'Hooks/useAuth'
import { useCompany, useModal } from 'Hooks'
import { InputMessageError } from 'Organisms/InputMessageError'
import { COLOR_RED } from 'constant/colors'
import { LOGIN_ROUTER } from 'constant/router'
import { CURRENT_USER_STORAGE } from 'Utils/const/localstorage'
import { Mixpanel } from 'Utils/Mixpanel'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

// Deleting the company deletes the caller's own user too, so there is no
// session left to return to - sign out the same way the navbar's logout does
// (clear local state before Amplify, since a hosted-UI session redirects away
// before anything after `signOut()` would run) and send them to Login.
const signOutAfterDeletion = async ({ updateUser, navigate }) => {
  Mixpanel.reset()
  localStorage.removeItem(CURRENT_USER_STORAGE)
  localStorage.removeItem('amplify-signin-with-hostedUI')
  updateUser(null)

  try {
    await signOut()
  } catch (e) {
    console.error(e)
  } finally {
    navigate(LOGIN_ROUTER)
  }
}

const DeleteAccountModal = ({ companyName, onClose }) => {
  const { Modal, hide } = useModal({ identifier: 'modalDeleteAccount', visible: true, setVisible: onClose })
  const { loadingCompanyHook, deleteCompanyHook } = useCompany()
  const { updateUser } = useSessionStore((state) => ({ updateUser: state.updateUser }))
  const navigate = useNavigate()

  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState('')

  const isConfirmed = confirmation.trim() === companyName

  const handleDelete = async () => {
    if (!isConfirmed) { return }

    setError('')
    const res = await deleteCompanyHook({ confirmation: confirmation.trim() })

    if (!res.success) {
      return setError(res.message)
    }

    await signOutAfterDeletion({ updateUser, navigate })
  }

  return (
    <Modal center>
      <div className="modal-content p-5" style={{ maxWidth: 450 }}>
        <div className="modal-header">
          <div className="row justify-content-center text-center gy-2">
            <div className="col-12">
              <WarningOctagon size={48} color={COLOR_RED} weight="bold" />
            </div>
            <div className="col-12">
              <h3 className="modal-title fs-5">Delete your account</h3>
            </div>
            <div className="col-12">
              <p className="m-0">
                This permanently deletes <strong>{companyName}</strong> and everything in it &mdash; projects, time
                entries, clients, billing history, and every team member&apos;s account. This cannot be undone.
              </p>
            </div>
          </div>
          <button type="button" className="btn-close" onClick={() => hide()} disabled={loadingCompanyHook} />
        </div>

        <div className="modal-body d-grid">
          {error && <InputMessageError error={error} />}

          <label className="form-label" htmlFor="inputDeleteConfirmation">
            Type <strong>{companyName}</strong> to confirm
          </label>
          <input
            type="text"
            id="inputDeleteConfirmation"
            className="form-control mb-4"
            placeholder={companyName}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            autoComplete="off"
          />

          <div className="row justify-content-between">
            <div className="col-auto">
              <button type="button" onClick={() => hide()} className="btn btn-secondary" disabled={loadingCompanyHook}>
                Cancel
              </button>
            </div>
            <div className="col-auto">
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={!isConfirmed || loadingCompanyHook}>
                {loadingCompanyHook ? 'Deleting...' : 'Delete account'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default DeleteAccountModal
